<template>
  <v-card
    class="ma-1 pb-6"
    style="overflow-x: scroll"
    width="100%"
  >
    <div
      v-if="error"
      style="color: red; font-size: larger"
    >
      {{ error }}
    </div>

    <v-row class="mb-0 ml-0 elevation-5">
      <v-col cols="auto">
        <v-switch
          v-model="showImage"
          label="Show structure"
          title="Show structure image"
        />
      </v-col>
      <v-col cols="1">
        <v-select
          v-model="fingerprintName"
          :items="this.fingerprints"
          item-value="id"
          label="Fingerprint:"
          title="Fingerprint name"
        />
      </v-col>
      <v-col
        v-if="fingerprintName === 'padel' || fingerprintName === 'rdkit' || fingerprintName === 'toxprints'"
        cols="1"
      >
        <v-select
          v-if="fingerprintName === 'rdkit'"
          v-model="rdkitType"
          :items="['ecfp', 'fcfp']"
          label="Type:"
          title="RDKit type"
        />
        <v-select
          v-if="fingerprintName === 'rdkit'"
          v-model="rdkitRadius"
          :items="[2, 3, 4]"
          label="Radius:"
          title="RDKit radius"
        />
        <v-select
          v-if="fingerprintName === 'rdkit'"
          v-model="rdkitBits"
          :items="[512, 1024, 2048]"
          label="Bits:"
          title="RDKit bits"
        />
        <v-select
          v-if="fingerprintName === 'toxprints'"
          v-model="toxprintsProfile"
          :items="['AIM_v1_0', 'toxprint_V2_0_r711', 'TxP_PFAS_v1_0_5']"
          label="Profile:"
          title="ToxPrints profile"
        />
        <v-checkbox
          v-if="fingerprintName === 'padel'"
          v-model="padelCompute2D"
          hide-details
          label="Compute 2D"
          title="PaDEL compute 2D"
        />
        <v-checkbox
          v-if="fingerprintName === 'padel'"
          v-model="padelCompute3D"
          hide-details
          label="Compute 3D"
          title="PaDEL compute 3D"
        />
        <v-checkbox
          v-if="fingerprintName === 'padel'"
          v-model="padelComputeFingerprints"
          hide-details
          label="Compute Fingerprints"
          title="PaDEL compute Fingerprints"
        />
      </v-col>

      <v-col cols="1">
        <v-select
          v-model="scoreName"
          :items="this.getScores"
          item-value="id"
          label="Metric:"
          title="Score name"
        />
      </v-col>
      <v-col
        v-if="scoreName === 'tversky'"
        cols="auto"
      >
        <b-form-input
          v-model="tverskyI"
          max="10"
          min="0"
          step="1"
          style="width: 10em"
          type="range"
        />
        <div
          class="mt-2"
          style=""
        >
          alpha = {{ tverskyI === 0 ? '1' : '0.' + (10 - tverskyI) }} beta =
          {{ tverskyI === 10 ? '1' : '0.' + tverskyI }}
        </div>
      </v-col>

      <v-col cols="1">
        <v-select
          v-model="showLimit"
          :items="['All', '1', '0.9', '0.8', '0.7', '0.6', '0.5', '0.4', '0.3', '0.2', '0.1']"
          label="Cutoff"
          title="Do not show structures if no similarity greater than"
        />
      </v-col>
      <v-col cols="1">
        <v-select
          v-model="sssLimit"
          :items="['No limit', '20', '50', '100']"
          label="Limit"
          title="Limit structures"
        />
      </v-col>

      <v-col class="d-flex justify-end">
        <div class="mr-6 mt-3">
          <v-chip
            small
            style="vertical-align: middle"
          >
            {{ filtered.length }}
          </v-chip>
          /
          <v-chip small>{{ similarity.length }}</v-chip>
        </div>
        <v-btn-toggle
          class="mr-6 mt-3"
          dense
        >
          <v-btn
            v-b-tooltip
            title="Export to CSV"
            @click="onExport('CSV')"
            aria-label="Export to CSV"
          >
            <v-icon>mdi-file-delimited-outline</v-icon>
          </v-btn>
          <v-btn
            v-b-tooltip
            title="Export to XLSX"
            @click="onExport('XLSX')"
            aria-label="Export to XLSX"
          >
            <v-icon>mdi-file-excel-outline</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          class="mr-6 mt-3"
          dense
        >
          <v-btn
            v-b-tooltip
            :disabled="selectedStructures.length === similarity.length + 1"
            title="Select All"
            @click="onSelectAllStructures"
            aria-label="Select All"
          >
            <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
          </v-btn>
          <v-btn
            v-b-tooltip
            :disabled="selectedStructures.length === 0"
            title="Select None"
            @click="onClearSelection"
            aria-label="Select None"
          >
            <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
          </v-btn>
        </v-btn-toggle>
        <div class="mr-6 mt-3">
          <TransferMenu
            :records="recordsForTransfer"
            only-icon
            hide-add-button
          />
        </div>
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <table class="table table-bordered table-records">
      <thead class="table-secondary">
        <tr>
          <th style="width: 1%">structure</th>
          <th style="width: 10%">dtxsid</th>
          <th style="width: 40%">name</th>
          <th style="width: 10%">relationship</th>
          <th style="width: 10%">similarity</th>
        </tr>
      </thead>
      <tr>
        <td class="p-2">
          <ChemCard
            :item="this.selection"
            :show-hazard="true"
            :show-image="showImage"
            show-check
            @check-click="onStructureCheckboxClick"
          />
        </td>
        <td>
          <a
            :href="'https://comptox.epa.gov/dashboard/chemical/details/' + this.selection.sid"
            target="_blank"
          >
            {{ this.selection.sid }}
          </a>
        </td>
        <td>
          {{ this.selection.name }}
        </td>
        <td>parent</td>
        <td>NA</td>
      </tr>
      <tr
        v-for="(item1, index1) in filtered"
        :key="index1"
      >
        <td class="p-2">
          <ChemCard
            :item="item1.chemical"
            :show-hazard="true"
            :show-image="showImage"
            show-check
            @check-click="onStructureCheckboxClick"
          />
        </td>
        <td>
          <a
            :href="'https://comptox.epa.gov/dashboard/chemical/details/' + item1.chemical.sid"
            target="_blank"
          >
            {{ item1.chemical.sid }}
          </a>
        </td>
        <td>
          {{ item1.chemical.name }}
        </td>
        <td>target</td>
        <td :class="item1.similarity.cl">
          {{ item1.similarity.sim }}
        </td>
      </tr>
    </table>
  </v-card>
</template>

<script>
import { download, getDatedFilename, getErrorMessage, RESOLVER_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import ChemCard from '@/components/chemical/ChemCard.vue'
import TransferMenu from '@/components/TransferMenu.vue'
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash-es'

export default {
  components: { TransferMenu, ChemCard },

  data: () => ({
    error: null,
    similarity: [],
    fingerprints: [],
    fingerprintsList: [
      {
        id: 'indigo',
        text: 'Indigo',
        defScore: 'tanimoto',
        scores: [
          { id: 'tanimoto', text: 'Tanimoto' },
          { id: 'normalized-edit', text: 'Normalized' },
          {
            id: 'tversky',
            text: 'Tversky',
          },
          { id: 'euclid-sub', text: 'Euclid' },
        ],
      },
      {
        id: 'toxprints',
        text: 'ToxPrints',
        defScore: 'cosine',
        scores: [
          { id: 'tanimoto', text: 'Tanimoto' },
          { id: 'tversky', text: 'Tversky' },
          {
            id: 'euclid-sub',
            text: 'Euclid',
          },
          { id: 'cosine', text: 'Cosine' },
        ],
      },
      {
        id: 'rdkit',
        text: 'RDKit',
        defScore: 'cosine',
        scores: [
          { id: 'tanimoto', text: 'Tanimoto' },
          { id: 'tversky', text: 'Tversky' },
          {
            id: 'euclid-sub',
            text: 'Euclid',
          },
          { id: 'cosine', text: 'Cosine' },
        ],
      },
      {
        id: 'webtest',
        text: 'WebTEST',
        defScore: 'cosine',
        scores: [
          { id: 'euclid', text: 'Euclid' },
          { id: 'cosine', text: 'Cosine' },
        ],
      },
      {
        id: 'padel',
        text: 'PaDEL',
        defScore: 'cosine',
        scores: [
          { id: 'euclid', text: 'Euclid' },
          { id: 'cosine', text: 'Cosine' },
        ],
      },
      {
        id: 'mordred',
        text: 'Mordred',
        defScore: 'cosine',
        scores: [
          { id: 'euclid', text: 'Euclid' },
          { id: 'cosine', text: 'Cosine' },
        ],
      },
    ],
    rdkitType: 'ecfp',
    rdkitRadius: 2,
    rdkitBits: 512,
    padelCompute2D: true,
    padelCompute3D: false,
    padelComputeFingerprints: false,
    toxprintsProfile: 'toxprint_V2_0_r711',
    scoreName: 'tanimoto',
    fingerprintName: 'indigo',
    loading: false,
    tverskyI: '5',
    showImage: true,
    showLimit: 'All',
    selectedStructures: [],
    sssLimit: 'No limit',
  }),

  props: {
    selection: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(['feature', 'getDescriptors', 'getCheckedStructures']),
    filtered() {
      return this.similarity.filter(
        (item, idx) =>
          (this.showLimit === 'All' || item.similarity.sim >= this.showLimit) &&
          (this.sssLimit === 'No limit' || this.sssLimit > idx),
      )
    },
    getScores() {
      let o = this.fingerprints.find((f) => f.id === this.fingerprintName)
      return o ? o.scores : []
    },
    recordsForTransfer() {
      const records = cloneDeep(
        this.selectedStructures.filter(
          (item) =>
            this.selection.chemId === item.chemId ||
            this.filtered.findIndex((o) => o.chemical.chemId === item.chemId) !== -1,
        ),
      )
      records.forEach((r) => {
        r.id = r.sid
        r.checked = true
        r.similarity = this.selection.chemId === r.chemId ? 1 : this.getSimByChemId(r.chemId)
        delete r.hazard
        delete r.authority
      })
      return records
    },
  },

  watch: {
    selection() {
      this.onNewSelection()
      this.getSimilarity()
    },

    fingerprintName: {
      handler() {
        const fingerprint = this.fingerprints.find((f) => f.id === this.fingerprintName)
        if (!fingerprint.scores.find((s) => s.id === this.scoreName)) this.scoreName = fingerprint.defScore

        this.getSimilarity()
      },
    },
    scoreName() {
      this.getSimilarity()
    },
    tverskyI() {
      this.getSimilarity()
    },
    rdkitType() {
      this.getSimilarity()
    },
    rdkitRadius() {
      this.getSimilarity()
    },
    rdkitBits() {
      this.getSimilarity()
    },
    padelCompute2D() {
      this.getSimilarity()
    },
    padelCompute3D() {
      this.getSimilarity()
    },
    padelComputeFingerprints() {
      this.getSimilarity()
    },
    toxprintsProfile() {
      this.getSimilarity()
    },
  },

  methods: {
    onNewSelection() {
      this.similarity = []
      this.selectedStructures = []
      this.selection.checked = true
      this.selection.similarity = null
      this.selectedStructures.push(this.selection)
    },

    onExport(format) {
      this.loading = true
      this.$axios
        .post(
          `${RESOLVER_URL}/exportsimilaritylist?format=${format}&showImage=${this.showImage}&limit=${this.showLimit}`,
          {
            main: this.selection,
            _chemicals: this.getCheckedStructures,
            fingerprintName: this.fingerprintName,
            scoreName: this.scoreName,
            tverskyI: this.tverskyI,
            rdkitType: this.rdkitType,
            rdkitRadius: this.rdkitRadius,
            rdkitBits: this.rdkitBits,
            padelCompute2D: this.padelCompute2D,
            padelCompute3D: this.padelCompute3D,
            padelComputeFingerprints: this.padelComputeFingerprints,
            toxprintsProfile: this.toxprintsProfile,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          this.loading = false
          const filename = getDatedFilename('exportsimilarity', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          this.loading = false
          this.error = getErrorMessage(error)
          showErrorToast(error, { title: 'Error during export' })
        })
    },

    getSimilarity() {
      this.loading = true
      this.error = ''
      this.$axios
        .post(`${RESOLVER_URL}/getsimilaritylist`, {
          main: this.selection,
          _chemicals: this.getCheckedStructures,
          fingerprintName: this.fingerprintName,
          scoreName: this.scoreName,
          tverskyI: this.tverskyI,
          rdkitType: this.rdkitType,
          rdkitRadius: this.rdkitRadius,
          rdkitBits: this.rdkitBits,
          padelCompute2D: this.padelCompute2D,
          padelCompute3D: this.padelCompute3D,
          padelComputeFingerprints: this.padelComputeFingerprints,
          toxprintsProfile: this.toxprintsProfile,
        })
        .then((response) => {
          response.data.forEach((o) => {
            o.chemical.checked = true
            this.selectedStructures.push(o.chemical)
          })

          this.similarity = response.data

          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.error = getErrorMessage(error)
        })
    },
    getSimByChemId(chemId) {
      return this.similarity.find((item) => chemId === item.chemical.chemId).similarity.sim
    },
    onStructureCheckboxClick(structure) {
      const pos = this.selectedStructures.findIndex((e) => e.chemId === structure.chemId)
      if (pos !== -1) {
        this.selectedStructures.splice(pos, 1)
        structure.checked = false
      } else {
        this.selectedStructures.push(structure)
        structure.checked = true
      }
    },
    onSelectAllStructures() {
      this.selectedStructures = []

      this.selection.checked = true
      this.selectedStructures.push(this.selection)

      this.filtered.forEach((o) => {
        o.chemical.checked = true
        this.selectedStructures.push(o.chemical)
      })
    },

    onClearSelection() {
      this.selectedStructures.forEach((o) => {
        o.checked = false
      })
      this.selectedStructures = []
    },
  },

  created() {
    this.onNewSelection()
  },

  mounted() {
    this.fingerprints = this.fingerprintsList.filter((item) => {
      if (item.id === 'indigo') return true
      return this.getDescriptors && this.getDescriptors.descriptors
        ? this.getDescriptors.descriptors.findIndex((d) => item.id === d.version.name) !== -1
        : false
    })
    this.getSimilarity()
  },
}
</script>
